import { EnvironmentOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { api } from "@common/api";
import { InputMask } from "@components/atoms/InputMask/InputMask";
import { Button, Form, Input, message, Typography } from "antd";
import { useState } from "react";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export const ContactPage = () => {
  const [form] = Form.useForm();
  const [showThankyou, setShowThankyou] = useState(false);
  const [lastSubject, setLastSubject] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFinish = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      const dataToSend = {
        ...values,
        subject: "Request via Landing Page"
      };
      await api.post("/contact", dataToSend);
      setLastSubject("Request via Landing Page");
      setShowThankyou(true);
      message.success("Your message has been sent successfully!");
      form.resetFields();
    } catch (error) {
      console.error("Error sending message:", error);
      message.error("Unable to send your message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const ThankYouMessage = () => (
    <div className="bg-rock-grey p-6 text-center">
      <div className="mb-6">
        <MailOutlined className="text-primary text-5xl" />
      </div>
      <Title level={3} className="text-white mb-6">
        THANK YOU FOR REACHING OUT
      </Title>
      <Paragraph className="text-secondary text-lg mb-8">
        Your message regarding "{lastSubject}" has been received. Our team will respond as soon as possible.
      </Paragraph>
      <Button type="primary" onClick={() => setShowThankyou(false)}>
        SEND ANOTHER MESSAGE
      </Button>
    </div>
  );

  return (
    <div className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat">
      {/* Hero Section */}
      <section className="w-full h-[400px]">
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <div className="font-displayed !font-normal text-white text-6xl tracking-wider text-center">
            CONTACT US
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="w-full bg-black py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-charcoal flex flex-col items-center p-8 text-center">
              <PhoneOutlined className="text-orangicide text-4xl mb-4" />
              <Title level={3} className="font-orator !font-normal text-white">CALL US</Title>
              <Paragraph className="font-orator !text-gray-400 !text-lg !mb-1">866.308.5465</Paragraph>
              <Paragraph className="font-orator !text-gray-400 !text-base">Monday - Friday: 7am - 5pm</Paragraph>
            </div>
            <div className="bg-charcoal flex flex-col items-center p-8 text-center">
              <MailOutlined className="text-orangicide text-4xl mb-4" />
              <Title level={3} className="font-orator !font-normal text-white">EMAIL US</Title>
              <Paragraph className="font-orator !text-gray-400 !text-lg !mb-1">info@modlink.com</Paragraph>
              <Paragraph className="font-orator !text-gray-400 !text-base">We respond within 24h</Paragraph>
            </div>
            <div className="bg-charcoal flex flex-col items-center p-8 text-center">
              <EnvironmentOutlined className="text-orangicide text-4xl mb-4" />
              <Title level={3} className="font-orator !font-normal text-white">MAILING ADDRESS</Title>
              <Paragraph className="font-orator !text-gray-400 !text-lg">PO Box 1921<br />Truckee, CA 96160</Paragraph>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="send-message" className="w-full py-24">
        <div className="container mx-auto px-4">
          <Title level={1} className="font-orator text-orangicide text-center tracking-wider pb-4">SEND US A MESSAGE</Title>
          <div className="bg-white w-full md:w-130 mx-auto p-6 mb-8">
            {!showThankyou ? (
              <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={true} disabled={isSubmitting}>
                <Form.Item label="Full Name" name="name" rules={[{ required: true, message: "please enter your name" }]}>
                  <div><Input placeholder="full name" /></div>
                </Form.Item>
                <Form.Item
                  label="Email address"
                  name="email"
                  rules={[
                    { required: true, message: "please enter your email" },
                    { type: 'email', message: "please enter a valid email address" }
                  ]}
                >
                  <div><Input placeholder="email@email.com" /></div>
                </Form.Item>
                <Form.Item label="Phone number" name="phone" rules={[{ required: true, message: "please enter your phone number" }]}>
                  <InputMask mask="usphone" placeholder="000 000-0000" />
                </Form.Item>
                <Form.Item label="Message" name="message" rules={[{ required: true, message: "please enter your message" }]}>
                  <div><TextArea rows={4} placeholder="your message" /></div>
                </Form.Item>
                <div className="text-center pt-4"><button type="submit" disabled={isSubmitting} className="font-displayed !font-normal !text-white text-2xl w-60 h-10 btn-orangicide cursor-pointer">
                  {isSubmitting ? "SENDING..." : "SEND MESSAGE"}
                </button></div>
              </Form>
            ) : (
              <ThankYouMessage />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};